// Steps 1–4 call the individual ootils step functions so each intermediate can be
// displayed; the final assembly (step 5/6) delegates to the shared orchestrator.
import {
  autoGenFilterConfigsFromTpl,
  buildFilterConfigurations,
  compareAndGroupBlocks,
  extractAndOrganizeBlocks,
  processAuthorAndCommonFilters,
  buildDocFieldGroupedOptions,
  enrichCommonAnnotationFilters,
  _self_managed_getFixedAnnoTagBlock,
  _self_managed_getFixedAnnoRollupBlocks,
  TEMP_removeDuplicateFilters,
} from "@okf/ootils";

const EMPTY_RESULT = {
  extractedBlocks: null,
  authorAndCommonFilters: null,
  groupedBlocks: null,
  deduplicatedGroups: null,
  builtSections: null,
  finalSections: null,
  result: null,
};

export const runAutoGenFilterPipeline = ({
  selectedTpls,
  allTpls,
  filterScopes,
  isSelfManagedTenant,
  annotationTagsCount,
  smTagTypesConfig,
  flatDocFilters = false,
  hasNewTopicModelling = false,
  isSplitMode = false,
}) => {
  const normalizedSelectedTpls = Array.isArray(selectedTpls) ? selectedTpls : [];
  const normalizedAllTpls = Array.isArray(allTpls) ? allTpls : [];

  if (normalizedSelectedTpls.length === 0) return EMPTY_RESULT;

  const stages = { ...EMPTY_RESULT };

  // ── Step 1: Extract and organize blocks ─────────────────────────────
  try {
    stages.extractedBlocks = extractAndOrganizeBlocks(normalizedSelectedTpls, normalizedAllTpls, { smTagTypesConfig });
  } catch (err) {
    console.error("[FiltersPlayground] Step 1 (extractAndOrganizeBlocks) failed:", err);
    return stages;
  }

  // ── Step 2: Author & common filters ─────────────────────────────────
  let commonAnnotationFilters = [];
  let commonDocumentFilters = [];
  let authorTagConfigs = [];

  try {
    const allAnnoEnabledBlocks = filterScopes.includes("anno")
      ? (stages.extractedBlocks.annoEnabledBlocks || [])
          .flatMap(item => item.blocks || [])
          .reduce((acc, block) => {
            if (!acc.find(b => b.valuePath === block.valuePath)) {
              acc.push(block);
            }
            return acc;
          }, [])
      : [];

    const step2Result = processAuthorAndCommonFilters(normalizedAllTpls, filterScopes, allAnnoEnabledBlocks);
    authorTagConfigs = step2Result.authorTagConfigs;
    const _rawCommonAnnotationFilters = step2Result.commonAnnotationFilters;
    commonDocumentFilters = step2Result.commonDocumentFilters;

    // Both derived by the same ootils helpers the sidebar and AI Chat menu use,
    // so the displayed stage matches what actually ships.
    const docFieldGroupedOptions = filterScopes.includes("anno")
      ? buildDocFieldGroupedOptions({
          annoEnabledBlocks: stages.extractedBlocks.annoEnabledBlocks || [],
          allTpls: normalizedAllTpls,
        })
      : [];

    commonAnnotationFilters = enrichCommonAnnotationFilters({
      commonAnnotationFilters: _rawCommonAnnotationFilters,
      docFieldGroupedOptions,
      isSplitMode,
    });

    stages.authorAndCommonFilters = { authorTagConfigs, commonAnnotationFilters, commonDocumentFilters };
  } catch (err) {
    console.error("[FiltersPlayground] Step 2 (processAuthorAndCommonFilters) failed:", err);
    return stages;
  }

  // ── Step 3: Compare & group blocks ──────────────────────────────────
  let annoTagGroups = [];
  let annoRollupGroups = [];
  let combinedDocumentGroups = [];

  try {
    annoTagGroups =
      filterScopes.includes("anno")
        ? isSelfManagedTenant
          ? _self_managed_getFixedAnnoTagBlock(normalizedSelectedTpls, annotationTagsCount)
          : compareAndGroupBlocks(stages.extractedBlocks.annoTagBlocks)
        : [];

    annoRollupGroups =
      filterScopes.includes("anno")
        ? isSelfManagedTenant
          ? _self_managed_getFixedAnnoRollupBlocks({ selectedTpls: normalizedSelectedTpls, allTpls: normalizedAllTpls, annotationTagsCount })
          : compareAndGroupBlocks(stages.extractedBlocks.annoRollupBlocks)
        : [];

    combinedDocumentGroups =
      filterScopes.includes("doc")
        ? compareAndGroupBlocks(stages.extractedBlocks.combinedDocumentBlocks)
        : [];

    stages.groupedBlocks = { annoTagGroups, annoRollupGroups, combinedDocumentGroups };
  } catch (err) {
    console.error("[FiltersPlayground] Step 3 (compareAndGroupBlocks) failed:", err);
    return stages;
  }

  // ── Step 3b: Deduplicate ────────────────────────────────────────────
  let deduplicatedAnnoTagGroups = [];
  let deduplicatedAnnoRollupGroups = [];
  let deduplicatedCombinedDocumentGroups = [];

  try {
    deduplicatedAnnoTagGroups = TEMP_removeDuplicateFilters(annoTagGroups);
    deduplicatedAnnoRollupGroups = TEMP_removeDuplicateFilters(annoRollupGroups);
    deduplicatedCombinedDocumentGroups = TEMP_removeDuplicateFilters(combinedDocumentGroups);

    stages.deduplicatedGroups = {
      annoTagGroups: deduplicatedAnnoTagGroups,
      annoRollupGroups: deduplicatedAnnoRollupGroups,
      combinedDocumentGroups: deduplicatedCombinedDocumentGroups,
    };
  } catch (err) {
    console.error("[FiltersPlayground] Step 3b (TEMP_removeDuplicateFilters) failed:", err);
    return stages;
  }

  // ── Step 4: Build filter configurations ─────────────────────────────
  let annotationTagSections = [];
  let annotationRollupSections = [];
  let documentTagSections = [];

  try {
    annotationTagSections = buildFilterConfigurations({
      groups: deduplicatedAnnoTagGroups,
      type: "annoTags",
      selectedTpls: normalizedSelectedTpls,
      allTpls: normalizedAllTpls,
      isRollup: false,
      isAnno: true,
    });

    annotationRollupSections = buildFilterConfigurations({
      groups: deduplicatedAnnoRollupGroups,
      type: "annoTags",
      selectedTpls: normalizedSelectedTpls,
      allTpls: normalizedAllTpls,
      isRollup: true,
      isAnno: true,
    });

    documentTagSections = buildFilterConfigurations({
      groups: deduplicatedCombinedDocumentGroups,
      type: "tags",
      selectedTpls: normalizedSelectedTpls,
      allTpls: normalizedAllTpls,
      isRollup: false,
      isAnno: false,
    });

    stages.builtSections = { annotationTagSections, annotationRollupSections, documentTagSections };
  } catch (err) {
    console.error("[FiltersPlayground] Step 4 (buildFilterConfigurations) failed:", err);
    return stages;
  }

  // ── Step 5/6: Final assembly ────────────────────────────────────────
  // Delegated to ootils so the playground shows exactly what the Discovery sidebar
  // and the AI Chat filter menu receive. Steps 1–4 above stay instrumented (same
  // ootils step functions) purely to expose the intermediates for inspection.
  try {
    stages.result = autoGenFilterConfigsFromTpl({
      selectedTpls: normalizedSelectedTpls,
      allTpls: normalizedAllTpls,
      filterScopes,
      isSelfManagedTenant,
      annotationTagsCount,
      smTagTypesConfig,
      flatDocFilters,
      hasNewTopicModelling,
      isSplitMode,
    });

    const bySectionId = (id) => stages.result.find((s) => s.sectionId === id) || null;
    stages.finalSections = {
      metadata: bySectionId("metadataSection"),
      annotation: bySectionId("annotationTagsSection"),
      document: bySectionId("documentTagsSection"),
    };
  } catch (err) {
    console.error("[FiltersPlayground] Step 5/6 (final assembly) failed:", err);
    return stages;
  }

  return stages;
};
